import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import '@schmancy/primitives/busy'
import { css, html } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ref, createRef } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { of, EMPTY, Observable } from 'rxjs'
import { switchMap, tap, catchError, takeUntil, finalize, shareReplay } from 'rxjs/operators'

/**
 * Minimal ambient surface for the parts of the Google Maps JavaScript SDK
 * this file and `schmancy-place-picker` touch. The upstream
 * `@types/google.maps` package isn't a dependency here — the SDK is loaded
 * at runtime from a `<script>` tag, not installed, so there is nothing to
 * import types from; these are typed loosely (`any` for options/results
 * bags) to match the shape the callback-style SDK actually hands back.
 *
 * `places` carries both the current Autocomplete Data API
 * (`AutocompleteSuggestion.fetchAutocompleteSuggestions` + `Place`) and the
 * legacy `AutocompleteService`/`PlacesServiceStatus`: the current API is
 * unavailable to any Cloud project created after 2025-03-01, but some
 * pre-existing projects haven't enabled "Places API (New)" yet and 403 on
 * it — `schmancy-place-picker` tries the current API first and falls back
 * to legacy per-instance, so both surfaces stay live here.
 */
interface GooglePlace {
	id: string
	formattedAddress?: string
	location?: { lat: () => number; lng: () => number }
	fetchFields: (options: { fields: string[] }) => Promise<{ place: GooglePlace }>
}

interface GooglePlacePrediction {
	placeId: string
	text: { text: string }
	toPlace: () => GooglePlace
}

interface GoogleMapsAPI {
	maps: {
		Map: new (element: HTMLElement, options: any) => any
		Marker: new (options: any) => any
		Geocoder: new () => {
			geocode: (
				request: { address?: string; location?: { lat: number; lng: number }; placeId?: string },
				callback: (results: any[] | null, status: string) => void,
			) => void
		}
		LatLng: new (lat: number, lng: number) => any
		MapTypeId: {
			ROADMAP: string
			SATELLITE: string
			HYBRID: string
			TERRAIN: string
		}
		event: {
			addListener: (instance: any, eventName: string, handler: (...args: any[]) => void) => { remove: () => void }
		}
		places: {
			AutocompleteSessionToken: new () => object
			AutocompleteSuggestion: {
				fetchAutocompleteSuggestions: (request: {
					input: string
					sessionToken?: object
				}) => Promise<{ suggestions: Array<{ placePrediction?: GooglePlacePrediction }> }>
			}
			AutocompleteService: new () => {
				getPlacePredictions: (
					request: { input: string },
					callback: (predictions: Array<{ place_id: string; description: string }> | null, status: string) => void,
				) => void
			}
			PlacesServiceStatus: { OK: string; ZERO_RESULTS: string }
		}
	}
}

declare global {
	interface Window {
		google?: GoogleMapsAPI
		initGoogleMaps?: () => void
		__schmancyGoogleMapsLoading?: Observable<boolean>
	}
}

export type SchmancyMapPinChangeEvent = CustomEvent<{ lat: number; lng: number }>

/**
 * Singleton loader for the Google Maps script tag. `schmancy-map` and
 * `schmancy-place-picker` both call `GoogleMapsLoader.load(apiKey)` — the
 * `shareReplay(1)` means the first caller injects the `<script>` tag and
 * every other caller (same page, same or different component) rides that
 * one network request and one `initGoogleMaps` callback.
 */
// oxlint-disable-next-line typescript/no-extraneous-class
export class GoogleMapsLoader {
	private static loading$?: Observable<boolean>

	static load(apiKey: string): Observable<boolean> {
		if (window.google?.maps) {
			return of(true)
		}

		if (this.loading$) {
			return this.loading$
		}

		this.loading$ = new Observable<boolean>(observer => {
			if (window.google?.maps) {
				observer.next(true)
				observer.complete()
				return
			}

			const script = document.createElement('script')
			// `places` is loaded alongside the base SDK — both schmancy-map's own
			// geocoder and schmancy-place-picker's Autocomplete Data API need it.
			// `loading=async` per https://goo.gle/js-api-loading.
			script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places&callback=initGoogleMaps&v=weekly`
			script.async = true
			script.defer = true

			window.initGoogleMaps = () => {
				observer.next(true)
				observer.complete()
			}

			script.addEventListener(
				'error',
				error => {
					console.error('Google Maps script loading error:', error)
					observer.error(
						new Error(
							'Failed to load Google Maps. Please check API key configuration and ensure the domain is authorized.',
						),
					)
				},
				{ once: true },
			)

			document.head.appendChild(script)
		}).pipe(shareReplay(1))

		return this.loading$
	}
}

/**
 * `<schmancy-map>` component
 *
 * A Google Maps component with an intuitive API for displaying interactive or static maps.
 * Supports both address strings (with automatic geocoding) and precise coordinates.
 *
 * @element schmancy-map
 * @fires pin-change - `{ lat, lng }` — only in `pickable` mode, when the marker is dragged or the map is clicked. `composed: true`.
 *
 * @example
 * <!-- Simple address -->
 * <schmancy-map address="Times Square, New York"></schmancy-map>
 *
 * @example
 * <!-- With coordinates -->
 * <schmancy-map latitude="40.758" longitude="-73.985" zoom="17"></schmancy-map>
 *
 * @example
 * <!-- Satellite view -->
 * <schmancy-map address="Grand Canyon" type="satellite" height="500px"></schmancy-map>
 *
 * @example
 * <!-- Static map -->
 * <schmancy-map address="Eiffel Tower, Paris" interactive="false" controls="false"></schmancy-map>
 *
 * @example
 * <!-- Pickable: draggable marker + click-to-place, both emit `pin-change` -->
 * <schmancy-map pickable latitude="40.758" longitude="-73.985" @pin-change=${e => console.log(e.detail)}></schmancy-map>
 */
@schmancyElement('schmancy-map')
export class SchmancyMap extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				position: relative;
				border-radius: var(--schmancy-radius-lg, 0.625rem); /* mirrors --radius-lg (rounded-lg) */
				overflow: hidden;
				background-color: var(--schmancy-sys-color-surface-container);
				color: var(--schmancy-sys-color-surface-on);
			}

			:host([height]) {
				height: var(--map-height);
			}

			.map-container {
				width: 100%;
				height: 100%;
				min-height: 400px;
			}

			.loading-container {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 100%;
				height: 100%;
				min-height: 400px;
				background-color: var(--schmancy-sys-color-surface-containerLow);
			}

			.error-container {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				width: 100%;
				height: 100%;
				min-height: 400px;
				padding: 24px;
				text-align: center;
				background-color: var(--schmancy-sys-color-surface-containerLow);
			}

			.error-icon {
				width: 48px;
				height: 48px;
				margin-bottom: 16px;
				color: var(--schmancy-sys-color-error-default);
			}

			.error-title {
				font-size: 18px;
				font-weight: 600;
				margin-bottom: 8px;
				color: var(--schmancy-sys-color-surface-on);
			}

			.error-message {
				font-size: 14px;
				color: var(--schmancy-sys-color-surface-onVariant);
				line-height: 1.5;
			}
		`,
	]
	/**
	 * Simple address string that automatically geocodes to display the location.
	 * Takes precedence over latitude/longitude if both are provided.
	 */
	@property({ type: String })
	address: string = ''

	/**
	 * Latitude coordinate for precise location.
	 * Used when address is not provided.
	 */
	@property({ type: Number })
	latitude?: number

	/**
	 * Longitude coordinate for precise location.
	 * Used when address is not provided.
	 */
	@property({ type: Number })
	longitude?: number

	/**
	 * Map zoom level. Higher numbers show more detail.
	 * @default 15
	 */
	@property({ type: Number })
	zoom: number = 15

	/**
	 * Height of the map with CSS unit (e.g., "400px", "50vh").
	 * @default "400px"
	 */
	@property({ type: String, reflect: true })
	height: string = '400px'

	/**
	 * Whether to show a marker at the location.
	 * @default true
	 */
	@property({ type: Boolean })
	marker: boolean = true

	/**
	 * Tooltip text for the location marker.
	 */
	@property({ type: String })
	markerTitle: string = ''

	/**
	 * Map display type.
	 * Options: "roadmap", "satellite", "hybrid", "terrain"
	 * @default "roadmap"
	 */
	@property({ type: String })
	type: 'roadmap' | 'satellite' | 'hybrid' | 'terrain' = 'roadmap'

	/**
	 * Whether users can interact with the map (pan, zoom, click).
	 * @default true
	 */
	@property({ type: Boolean })
	interactive: boolean = true

	/**
	 * Whether to show map controls (zoom buttons, fullscreen, etc.).
	 * @default true
	 */
	@property({ type: Boolean })
	controls: boolean = true

	/**
	 * Picker mode: the marker becomes draggable and clicking the map moves it
	 * there. Both gestures dispatch `pin-change` with the settled coordinates
	 * — the map itself never geocodes or reverse-geocodes the result, that's
	 * the caller's job (see `schmancy-place-picker`, which composes this).
	 * @default false
	 */
	@property({ type: Boolean })
	pickable: boolean = false

	/**
	 * Google Maps API key. Required for the map to load.
	 */
	@property({ type: String })
	apiKey: string = ''

	@state() private loading: boolean = false
	@state() private error: string = ''

	private mapRef = createRef<HTMLDivElement>()
	private map?: any
	private mapMarker?: any
	private geocoder?: any
	private intersectionObserver?: IntersectionObserver
	private hasLoadedMap = false
	private mapsEventListeners: Array<{ remove: () => void }> = []

	connectedCallback() {
		super.connectedCallback()

		// Set CSS custom property for height
		this.style.setProperty('--map-height', this.height)

		// Only load map when component becomes visible
		this.setupIntersectionObserver()
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect()
		}
		this.mapsEventListeners.forEach(listener => listener.remove())
		this.mapsEventListeners = []
	}

	private setupIntersectionObserver() {
		// Load map only when it's visible in the viewport
		this.intersectionObserver = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting && !this.hasLoadedMap) {
						this.hasLoadedMap = true
						this.loadMap()
						// Stop observing after loading
						this.intersectionObserver?.disconnect()
					}
				})
			},
			{
				root: null,
				rootMargin: '50px',
				threshold: 0.01,
			},
		)

		this.intersectionObserver.observe(this)
	}

	private loadMap() {
		of(null)
			.pipe(
				tap(() => {
					this.loading = true
					this.error = ''
				}),
				switchMap(() => {
					if (!this.apiKey) {
						throw new Error('Google Maps API key is required. Please provide it via the apiKey property.')
					}
					return GoogleMapsLoader.load(this.apiKey)
				}),
				switchMap(() => this.getCoordinates()),
				tap(coordinates => {
					// Store coordinates for later use
					this.pendingCoordinates = coordinates
				}),
				catchError(error => {
					console.error('Map loading error:', error)
					this.error = error.message || 'Failed to load map'
					return EMPTY
				}),
				finalize(() => {
					this.loading = false
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	private pendingCoordinates?: { lat: number; lng: number }

	private getCoordinates() {
		if (this.address) {
			return this.geocodeAddress(this.address)
		}

		if (this.latitude !== undefined && this.longitude !== undefined) {
			return of({ lat: this.latitude, lng: this.longitude })
		}

		throw new Error('Either address or latitude/longitude coordinates are required')
	}

	private geocodeAddress(address: string) {
		if (!this.geocoder) {
			this.geocoder = new window.google!.maps.Geocoder()
		}

		return new Promise<{ lat: number; lng: number }>((resolve, reject) => {
			this.geocoder.geocode({ address }, (results: any[], status: string) => {
				if (status === 'OK' && results[0]) {
					const location = results[0].geometry.location
					resolve({
						lat: location.lat(),
						lng: location.lng(),
					})
				} else {
					reject(new Error(`Geocoding failed: ${status}`))
				}
			})
		})
	}

	private initializeMap(coordinates: { lat: number; lng: number }) {
		if (!this.mapRef.value || !window.google?.maps) {
			return
		}

		const mapOptions = {
			center: coordinates,
			zoom: this.zoom,
			mapTypeId: this.getMapTypeId(),
			disableDefaultUI: !this.controls,
			gestureHandling: this.interactive ? 'cooperative' : 'none',
			zoomControl: this.controls,
			mapTypeControl: this.controls,
			scaleControl: this.controls,
			streetViewControl: this.controls,
			rotateControl: this.controls,
			fullscreenControl: this.controls,
			styles: this.interactive
				? undefined
				: [
						{
							featureType: 'poi',
							stylers: [{ visibility: 'off' }],
						},
					],
		}

		this.map = new window.google.maps.Map(this.mapRef.value, mapOptions)

		if (this.pickable) {
			this.mapsEventListeners.push(
				window.google.maps.event.addListener(
					this.map,
					'click',
					(e: { latLng: { lat: () => number; lng: () => number } }) => {
						this.movePickedMarkerTo({ lat: e.latLng.lat(), lng: e.latLng.lng() })
					},
				),
			)
		}

		if (this.marker) {
			this.addMarker(coordinates)
		}
	}

	private getMapTypeId(): string {
		const typeMap = {
			roadmap: window.google!.maps.MapTypeId.ROADMAP,
			satellite: window.google!.maps.MapTypeId.SATELLITE,
			hybrid: window.google!.maps.MapTypeId.HYBRID,
			terrain: window.google!.maps.MapTypeId.TERRAIN,
		}
		return typeMap[this.type] || typeMap.roadmap
	}

	private addMarker(coordinates: { lat: number; lng: number }) {
		if (!window.google?.maps || !this.map) {
			return
		}

		this.mapMarker = new window.google.maps.Marker({
			position: coordinates,
			map: this.map,
			title: this.markerTitle || this.address || 'Location',
			draggable: this.pickable,
		})

		if (this.pickable) {
			this.mapsEventListeners.push(
				window.google.maps.event.addListener(this.mapMarker, 'dragend', () => {
					const position = this.mapMarker.getPosition()
					this.emitPinChange({ lat: position.lat(), lng: position.lng() })
				}),
			)
		}
	}

	/** Pickable mode only: moves the existing marker (dropping one if none exists yet) and dispatches `pin-change`. */
	private movePickedMarkerTo(coordinates: { lat: number; lng: number }) {
		if (!window.google?.maps || !this.map) return

		if (this.mapMarker) {
			this.mapMarker.setPosition(coordinates)
		} else {
			this.addMarker(coordinates)
		}
		this.emitPinChange(coordinates)
	}

	private emitPinChange(coordinates: { lat: number; lng: number }) {
		this.dispatchEvent(
			new CustomEvent<SchmancyMapPinChangeEvent['detail']>('pin-change', {
				detail: coordinates,
				bubbles: true,
				composed: true,
			}),
		)
	}

	protected updated(changedProperties: Map<string, unknown>) {
		super.updated(changedProperties)

		if (changedProperties.has('height')) {
			this.style.setProperty('--map-height', this.height)
		}

		// Initialize map when loading completes and container is ready
		if (changedProperties.has('loading') && !this.loading && this.pendingCoordinates && !this.map) {
			// Wait for next frame to ensure map container is rendered
			requestAnimationFrame(() => {
				if (this.mapRef.value && this.pendingCoordinates) {
					this.initializeMap(this.pendingCoordinates)
					this.pendingCoordinates = undefined
				}
			})
		}

		// Update marker title if it changes
		if (changedProperties.has('markerTitle') && this.mapMarker) {
			this.mapMarker.setTitle(this.markerTitle || this.address || 'Location')
		}
	}

	// From here and never `updated()`: loadMap()'s pipeline synchronously writes
	// `loading`/`error` (both reactive) on subscribe, which after render schedules
	// a trailing update (Lit's `change-in-update` warning); here it folds into
	// the update in flight. Guarded on an already-built map, so it never runs
	// before the first render.
	protected willUpdate(changedProperties: Map<string, unknown>) {
		super.willUpdate(changedProperties)
		if (
			(changedProperties.has('address') ||
				changedProperties.has('latitude') ||
				changedProperties.has('longitude') ||
				changedProperties.has('type') ||
				changedProperties.has('zoom')) &&
			this.map &&
			this.hasLoadedMap
		) {
			this.loadMap()
		}
	}

	protected render() {
		return html`
			${when(
				this.loading,
				() => html`
					<div class="loading-container">
						<schmancy-spinner></schmancy-spinner>
					</div>
				`,
				() =>
					when(
						this.error,
						() => html`
							<div class="error-container">
								<svg class="error-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
									/>
								</svg>
								<div class="error-title">Map could not be loaded</div>
								<div class="error-message">${this.error}</div>
							</div>
						`,
						() => html` <div class="map-container" ${ref(this.mapRef)}></div> `,
					),
			)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-map': SchmancyMap
	}
}
